import React, { useEffect, useRef, useState } from "react";
import {
  FaAngleLeft,
  FaAngleRight,
  FaArrowUpRightFromSquare,
} from "react-icons/fa6";
import { Link } from "react-router-dom";

type PartnerItem = {
  imgSrc: string;
  partnerName: string;
  partnerUrl?: string;
};

function OurPartner() {
  return (
    <div className="flex w-11/12 flex-col items-center gap-6 border-t border-gray-400 pt-10">
      <h2 className="text-2xl font-bold lg:text-4xl">我們的合作夥伴</h2>
      <PartnerScroll />
      <Link to="/our_partner" className="btn bg-[#B2222C] text-white">
        查看更多
      </Link>
    </div>
  );
}

function PartnerScroll() {
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const partnerCardRef = useRef<HTMLElement>(null);
  const [partnerList, setPartnerList] = useState<PartnerItem[]>([]);
  const [numPageItem, setNumPageItem] = useState(0);

  const numPage =
    numPageItem > 0 ? Math.ceil(partnerList.length / numPageItem) : 0;
  const containerWidthPercent = numPage > 0 ? numPage * 100 : 100;
  const cardWidthPercent =
    numPage > 0 && numPageItem > 0 ? (1 / (numPageItem * numPage)) * 100 : 100;
  const canSlide = partnerList.length > numPageItem;
  const maxStartIndex = Math.max(partnerList.length - numPageItem, 0);
  const clampedStartIndex = Math.min(currentStartIndex, maxStartIndex);
  const reachedStart = clampedStartIndex === 0;
  const reachedEnd = clampedStartIndex >= maxStartIndex;
  const leftPosition =
    partnerCardRef.current == null
      ? 0
      : -(clampedStartIndex * partnerCardRef.current.offsetWidth);

  useEffect(() => {
    function handleWindowResize() {
      const windowWidth = document.documentElement.clientWidth;
      const newNumPageItem = windowWidth < 768 ? 1 : 3;
      setNumPageItem(newNumPageItem);
    }

    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  useEffect(() => {
    setCurrentStartIndex((prev) => Math.min(prev, maxStartIndex));
  }, [maxStartIndex]);

  useEffect(() => {
    async function load() {
      const res = await fetch(
        "https://yja.p360tec.com/api/content/getList-partner",
      );
      const json = await res.json();
      const result = json.result.objects?.[0] ?? [];

      setPartnerList(
        result.map((partner: Record<string, unknown>) => ({
          imgSrc: String(partner.image ?? ""),
          partnerName: String(partner.name ?? ""),
          partnerUrl:
            partner.name == "P360"
              ? "https://p360tec.com"
              : partner.name == "灣駒出行"
              ? "https://gbataxi.com"
              : partner.name == "Kirei Hada"
              ? "https://kirei-hada.com"
              : null,
        })),
      );
    }

    load();
  }, []);

  function handleMoveBackward() {
    if (!canSlide) {
      return;
    }
    setCurrentStartIndex((prev) => Math.max(prev - numPageItem, 0));
  }

  function handleMoveForward() {
    if (!canSlide) {
      return;
    }
    setCurrentStartIndex((prev) => Math.min(prev + numPageItem, maxStartIndex));
  }

  return (
    <div className="relative w-full px-3">
      <div className="rounded-[2rem] bg-gradient-to-br from-[#fff7f4] via-white to-[#f4ebe8] px-4 py-5 shadow-[0_22px_60px_rgba(126,47,38,0.12)] md:px-6 md:py-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ScrollButton
              direction="left"
              disabled={!canSlide || reachedStart}
              onClick={handleMoveBackward}
            />
            <ScrollButton
              direction="right"
              disabled={!canSlide || reachedEnd}
              onClick={handleMoveForward}
            />
          </div>
        </div>

        <div className="overflow-hidden">
          <div
            className="flex transition-all duration-500 ease-out"
            style={{
              width: `${containerWidthPercent}%`,
              transform: `translateX(${leftPosition}px)`,
            }}
          >
            {partnerList.map((partner, idx) => (
              <PartnerCard
                cardWidthPercent={cardWidthPercent}
                imgUrl={partner.imgSrc}
                key={`${partner.partnerName}-${idx}`}
                partnerName={partner.partnerName}
                partnerRef={idx === 0 ? partnerCardRef : undefined}
                partnerUrl={partner.partnerUrl}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScrollButton({
  direction,
  disabled,
  onClick,
}: {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={direction === "left" ? "Previous partners" : "Next partners"}
      className="border-[#B2222C]/15 flex h-11 w-11 items-center justify-center rounded-full border bg-white text-[#B2222C] shadow-sm transition hover:-translate-y-0.5 hover:bg-[#B2222C] hover:text-white disabled:translate-y-0 disabled:cursor-not-allowed disabled:border-[#B2222C]/10 disabled:bg-[#f3ece8] disabled:text-[#bda7a1]"
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {direction === "left" ? <FaAngleLeft /> : <FaAngleRight />}
    </button>
  );
}

function PartnerCard({
  imgUrl,
  partnerName,
  partnerRef,
  cardWidthPercent,
  partnerUrl,
}: {
  imgUrl: string;
  partnerName: string;
  partnerRef?: React.RefObject<HTMLElement | null>;
  cardWidthPercent: number;
  partnerUrl?: string;
}) {
  const cardContent = (
    <>
      <div className="flex h-36 items-center justify-center overflow-hidden rounded-[1.4rem] bg-white/90 p-5 shadow-inner md:h-44">
        <img
          src={imgUrl}
          className="max-h-full max-w-full object-contain transition duration-300 group-hover:scale-105"
          alt={partnerName}
        />
      </div>
      <div className="flex min-h-[7.5rem] flex-col justify-between gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#B2222C]/60">
            合作伙伴
          </p>
          <p className="text-lg font-bold leading-snug text-[#3b2723] md:text-xl">
            {partnerName}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm font-semibold text-[#8a4f46]">
          <span>{partnerUrl != null ? "遊覽網站" : "檔案"}</span>
          {partnerUrl != null && (
            <FaArrowUpRightFromSquare className="shrink-0" />
          )}
        </div>
      </div>
    </>
  );

  return (
    <article
      className="shrink-0 p-3"
      style={{ width: `${cardWidthPercent}%` }}
      ref={partnerRef}
    >
      {partnerUrl != null ? (
        <a
          href={partnerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex h-full min-h-[19rem] flex-col gap-5 rounded-[1.75rem] border border-white/70 bg-gradient-to-b from-[#fffdfb] to-[#f7efeb] p-5 shadow-[0_18px_40px_rgba(126,47,38,0.12)] transition duration-300 hover:-translate-y-1 hover:border-[#B2222C]/20 hover:shadow-[0_26px_55px_rgba(126,47,38,0.18)]"
        >
          {cardContent}
        </a>
      ) : (
        <div className="group flex h-full min-h-[19rem] flex-col gap-5 rounded-[1.75rem] border border-white/70 bg-gradient-to-b from-[#fffdfb] to-[#f7efeb] p-5 shadow-[0_18px_40px_rgba(126,47,38,0.12)]">
          {cardContent}
        </div>
      )}
    </article>
  );
}

export default OurPartner;
